/**
 * Contexto de Autenticación
 *
 * Maneja el estado de autenticación del usuario en toda la aplicación
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import awsConfig from '../aws-config';
import {
  createOrganization as createOrganizationAPI,
  createRestaurant as createRestaurantAPI,
  getMyOrganization,
  listMyRestaurants,
} from '../services/api';
import { storage } from '../utils/storage';

// Configurar Amplify
Amplify.configure(awsConfig);

// Almacenar tokens en cookies con atributos Secure y SameSite en lugar de localStorage.
// Nota: no son httpOnly (Amplify necesita leer los tokens desde JS), pero eliminan
// el vector localStorage y agregan protección CSRF via SameSite.
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:',
    path: '/resto/admin/',
    sameSite: 'strict',
    expires: 30,
  })
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Traduce el detalle específico que Cognito devuelve en err.message (en inglés) a un
// texto UX-friendly en español. Devuelve null si no reconoce el patrón, para que el
// caller decida el fallback. Ver NER-4183: InvalidParameterException/InvalidPasswordException
// traían la causa real (política de password, formato de teléfono/email) que el mapeo
// genérico pisaba, dejando al usuario sin saber qué campo corregir.
const translateCognitoDetail = (message) => {
  if (!message || typeof message !== 'string') return null;
  const msg = message.toLowerCase();

  // Política de contraseña (Cognito: "Password did not conform with policy: ...")
  if (
    msg.includes('password did not conform') ||
    msg.includes('password must') ||
    msg.includes('password not long')
  ) {
    if (msg.includes('symbol')) return 'La contraseña debe tener al menos un símbolo (ej: !@#$%).';
    if (msg.includes('uppercase')) return 'La contraseña debe tener al menos una letra mayúscula.';
    if (msg.includes('lowercase')) return 'La contraseña debe tener al menos una letra minúscula.';
    if (msg.includes('numeric') || msg.includes('number'))
      return 'La contraseña debe tener al menos un número.';
    if (msg.includes('long enough') || msg.includes('length'))
      return 'La contraseña debe tener al menos 8 caracteres.';
    return 'La contraseña no cumple los requisitos: mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo.';
  }

  // Formato de teléfono (E.164)
  if (msg.includes('phone')) {
    return 'Formato de teléfono inválido. Usá el formato internacional +54XXXXXXXXXX (E.164).';
  }

  // Formato de email
  if (msg.includes('email')) {
    return 'Formato de email inválido. Verificá que esté bien escrito (ej: nombre@dominio.com).';
  }

  // Cognito a veces reporta un email malformado como "username"
  if (msg.includes('username')) {
    return 'El email ingresado no es válido. Verificá que esté bien escrito.';
  }

  return null;
};

// Mapeo de errores de Cognito a mensajes en español
const translateCognitoError = (err) => {
  const errorMap = {
    NotAuthorizedException: 'Usuario o contraseña incorrectos',
    UserNotFoundException: 'No existe ninguna cuenta con ese email',
    UserNotConfirmedException:
      'Tu cuenta no ha sido confirmada. Revisa tu email para el código de verificación.',
    UsernameExistsException: 'Ya existe una cuenta con ese email',
    InvalidPasswordException: 'La contraseña no cumple los requisitos de seguridad',
    CodeMismatchException: 'El código de verificación es incorrecto',
    ExpiredCodeException: 'El código de verificación ha expirado. Solicita uno nuevo.',
    LimitExceededException: 'Demasiados intentos. Por favor espera antes de intentar nuevamente.',
    TooManyRequestsException: 'Demasiadas solicitudes. Por favor espera un momento.',
    TooManyFailedAttemptsException: 'Demasiados intentos fallidos. Por favor espera.',
    InvalidParameterException: 'Parámetros inválidos. Verifica los datos ingresados.',
    PasswordResetRequiredException: 'Debes restablecer tu contraseña. Revisa tu email.',
    UserLambdaValidationException: 'Error al validar el usuario.',
    AliasExistsException: 'Ya existe una cuenta con ese email.',
  };

  // Errores donde el detalle específico de Cognito importa (política de password, formato
  // de teléfono/email). Priorizar ese detalle traducido sobre el mensaje genérico, que
  // ocultaba la causa real y no le decía al usuario qué corregir. Ver NER-4183.
  const DETAIL_SENSITIVE = new Set([
    'InvalidParameterException',
    'InvalidPasswordException',
    'UserLambdaValidationException',
  ]);

  if (DETAIL_SENSITIVE.has(err.name)) {
    const detail = translateCognitoDetail(err.message);
    if (detail) return detail;
    // Patrón no reconocido: preferimos el mensaje crudo de Cognito antes que el genérico
    // que oculta la causa (aunque venga en inglés, es más útil para corregir el campo).
    if (err.message) return err.message;
  }

  return errorMap[err.name] || err.message;
};

// Deriva { role, groups, roleWasDefaulted } de los tokens de una sesion de
// Cognito. Logica identica a la que antes vivía duplicada e independiente en
// cada montaje de useUserRole() (ver comentario en el estado de AuthProvider).
const deriveRoleFromSession = (session) => {
  const accessPayload = session?.tokens?.accessToken?.payload || {};
  const idPayload = session?.tokens?.idToken?.payload || {};
  const cognitoGroups = accessPayload['cognito:groups'] || [];
  const primaryRole = cognitoGroups.length > 0 ? cognitoGroups[0] : null;
  const customRole = idPayload['custom:role'];
  const hasAssignedRole = Boolean(primaryRole || customRole);
  const effectiveRole = (primaryRole || customRole || 'COMANDA').toUpperCase();
  return {
    role: effectiveRole,
    groups: cognitoGroups,
    roleWasDefaulted: !hasAssignedRole,
  };
};

// Keys para localStorage
const PENDING_ORGANIZATION_KEY = 'pendingOrganizationData';
// NER-4257 follow-up (roadmap QA 2026-09-10 #4): datos del restaurante
// recolectados en el gate de Google (CreatingOrgPage), a la espera de que
// exista una sesion utilizable para crearlo vía API autenticada.
const PENDING_RESTAURANT_KEY = 'pendingRestaurantData';
const ORGANIZATION_ID_KEY = 'organizationId';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPasswordChallenge, setPendingPasswordChallenge] = useState(null);
  // NER-4257 follow-up (roadmap QA 2026-09-10 #2): rol/grupos derivados una
  // sola vez acá, dentro de checkUser, en lugar de que cada consumidor de
  // useUserRole() dispare su propio fetchAuthSession({forceRefresh:true})
  // independiente. Esos fetches concurrentes (ProtectedRoute, Sidebar,
  // ActivityBar, UnauthorizedPage montando useUserRole() casi al mismo
  // tiempo) causaban una condición de carrera: una instancia resolvía con
  // rol vacío -> ProtectedRoute mandaba a /unauthorized -> segundos después
  // otra instancia (la de UnauthorizedPage) resolvía bien -> auto-redirect a
  // '/'. De ahí el "flash" de Acceso No Autorizado que reporta Diego.
  const [role, setRole] = useState(null);
  const [groups, setGroups] = useState([]);
  const [roleWasDefaulted, setRoleWasDefaulted] = useState(false);

  // Verificar si hay un usuario autenticado al cargar la app
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh });

      // Derivar nombre/email a mostrar desde los claims del idToken. En el flujo
      // Google OAuth la sesión se restaura desde cookies (hard-redirect) y NO hay
      // signInDetails.loginId, por lo que el header caía a user.username = sub de
      // Cognito (UUID). El idToken siempre trae email (pool email-verified) y name.
      // Ver NER-4032 (follow-up NER-3781). NER-4030 (agents-dev) resolvía lo mismo;
      // se conserva la versión NER-4032 ya desplegada en prod (594ed5b).
      const idTokenPayload = session.tokens?.idToken?.payload || {};
      const email = currentUser.signInDetails?.loginId || idTokenPayload.email;
      const name = idTokenPayload.name || idTokenPayload.given_name;

      const initialRoleInfo = deriveRoleFromSession(session);
      setRole(initialRoleInfo.role);
      setGroups(initialRoleInfo.groups);
      setRoleWasDefaulted(initialRoleInfo.roleWasDefaulted);

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        attributes: currentUser.signInDetails?.loginId,
        name,
        email,
        tokens: {
          idToken: session.tokens?.idToken?.toString(),
          accessToken: session.tokens?.accessToken?.toString(),
          refreshToken: session.tokens?.refreshToken?.toString(),
        },
      });

      // RES-93: el partner (rol PARTNER) no es miembro de ninguna organización; usa solo su
      // portal. No se busca ni se recupera una organización para él.
      if (initialRoleInfo.role === 'PARTNER') {
        setOrganization(null);
        storage.remove(ORGANIZATION_ID_KEY);
        storage.remove('selectedOrganizationId');
        storage.remove('selectedRestaurantId');
        return;
      }

      // Verificar si el usuario tiene una organización
      try {
        const orgResponse = await getMyOrganization();

        // El backend devuelve { organizations: [...] }, tomar la primera
        const orgs = orgResponse.organizations || [];
        const savedOrgId = storage.get('selectedOrganizationId');
        const org = orgs.find((o) => o.organizationId === savedOrgId) || orgs[0];

        if (!org) {
          // NER-4257 follow-up (roadmap QA 2026-09-10 #1/#2): si el usuario
          // tiene datos pendientes de un signup reciente (localStorage) pero
          // createOrganizationAfterSignup nunca corrio — por ejemplo, confirmo
          // el codigo en otra pestana/sesion, o cerro la pestana de registro
          // antes de terminar — lo recuperamos aca. checkUser corre en TODO
          // login (a diferencia del flujo inline de RegisterPage, que solo
          // cubre "confirmar en la misma pestana"), asi que es el unico punto
          // por el que pasan todos los casos.
          const pendingDataString = storage.get(PENDING_ORGANIZATION_KEY);
          if (pendingDataString) {
            try {
              const pendingData = JSON.parse(pendingDataString);
              const createResult = await createOrganizationAPI(pendingData);
              const newOrg = createResult.organization;

              if (newOrg?.organizationId) {
                // Refrescar el token para que custom:role/custom:organizationId
                // (recien seteados por el backend) esten en el ID token.
                const refreshedSession = await fetchAuthSession({ forceRefresh: true });
                const refreshedRoleInfo = deriveRoleFromSession(refreshedSession);
                setRole(refreshedRoleInfo.role);
                setGroups(refreshedRoleInfo.groups);
                setRoleWasDefaulted(refreshedRoleInfo.roleWasDefaulted);

                // Hallazgo QA 2026-09-10 (ronda 2): estos storage.set() tienen que
                // pasar ANTES de intentar crear el restaurante. El interceptor de
                // `api` (services/api.js) arma el header X-Organization-Id leyendo
                // storage.get('selectedOrganizationId'), y el backend de
                // POST /restaurants EXIGE ese header (400 "Organization ID is
                // required" si falta, sin fallback al claim del JWT). En una
                // cuenta nueva ese storage key todavia no existe, asi que si
                // createRestaurantAPI se llama antes de setearlo, el request
                // siempre falla con 400 — no es intermitente, pasaba siempre en
                // este flujo. El catch de abajo lo silenciaba (solo un
                // console.warn), por eso el restaurante nunca quedaba creado y
                // el usuario terminaba completandolo a mano en /restaurant/new.
                setOrganization(newOrg);
                storage.set(ORGANIZATION_ID_KEY, newOrg.organizationId);
                storage.set('selectedOrganizationId', newOrg.organizationId);
                if (newOrg.name) storage.set('selectedOrganizationName', newOrg.name);
                if (newOrg.userRole) storage.set('userRole', newOrg.userRole);

                // Best-effort: crear el primer restaurante con los datos ya
                // recolectados en el wizard de registro. Si falla, no rompemos
                // el login — RequireRestaurant lo mandara a /restaurant/new.
                try {
                  const restaurantSlug = pendingData.restaurantSlug || '';
                  if (pendingData.restaurantName && restaurantSlug) {
                    const createRestResponse = await createRestaurantAPI({
                      name: pendingData.restaurantName,
                      slug: restaurantSlug,
                      address: pendingData.address || '',
                    });
                    const newRestaurantId =
                      createRestResponse?.restaurantId ||
                      createRestResponse?.restaurant?.restaurantId;
                    if (newRestaurantId) {
                      storage.set('selectedRestaurantId', newRestaurantId);
                      storage.set('selectedRestaurantName', pendingData.restaurantName);
                    }
                  }
                } catch (restErr) {
                  console.warn(
                    '[AuthContext] No se pudo crear el restaurante inicial (recuperacion post-login):',
                    restErr.message
                  );
                }

                storage.remove(PENDING_ORGANIZATION_KEY);
                return;
              }
            } catch (recoveryError) {
              console.error(
                '[AuthContext] Error recuperando organizacion pendiente post-login:',
                recoveryError.message
              );
              // Sigue el flujo normal de abajo (organization=null) si esto falla.
            }
          }

          setOrganization(null);
          storage.remove(ORGANIZATION_ID_KEY);
          storage.remove('selectedOrganizationId');
          storage.remove('selectedOrganizationName');
          storage.remove('userRole');
          storage.remove('allRestaurants');
          storage.remove('selectedRestaurantId');
          storage.remove('selectedRestaurantName');
          return;
        }

        setOrganization(org);

        // Guardar organizationId en localStorage para uso fuera del contexto
        if (org?.organizationId) {
          storage.set(ORGANIZATION_ID_KEY, org.organizationId);
          // Sincronizar selectedOrganizationId para que el interceptor de API
          // pueda enviar X-Organization-Id desde el primer request
          storage.set('selectedOrganizationId', org.organizationId);
          if (org.name) storage.set('selectedOrganizationName', org.name);
          if (org.userRole) storage.set('userRole', org.userRole);
          if (org.allRestaurants !== undefined) storage.set('allRestaurants', org.allRestaurants);
        }

        // Auto-seleccionar restaurante si no hay uno seleccionado
        try {
          const savedRestaurantId = storage.get('selectedRestaurantId');
          const restData = await listMyRestaurants(org.organizationId);
          let activeRestaurants = (restData.restaurants || []).filter(
            (r) => r.status === 'active' || r.status === 'inactive'
          );

          // NER-4257 follow-up (roadmap QA 2026-09-10 #4): si la organizacion
          // no tiene ningun restaurante todavia y hay datos pendientes del
          // gate de Google (CreatingOrgPage), este es el primer momento con
          // sesion + organizacion ya listas para crearlo via API autenticada.
          // Sin esto, un usuario que entro con Google quedaba con organizacion
          // pero cero restaurantes y quedaba flotando en /restaurant/new.
          if (activeRestaurants.length === 0) {
            const pendingRestaurantString = storage.get(PENDING_RESTAURANT_KEY);
            if (pendingRestaurantString) {
              try {
                const pendingRestaurant = JSON.parse(pendingRestaurantString);
                if (pendingRestaurant.restaurantName && pendingRestaurant.restaurantSlug) {
                  const createRestResponse = await createRestaurantAPI({
                    name: pendingRestaurant.restaurantName,
                    slug: pendingRestaurant.restaurantSlug,
                    address: pendingRestaurant.address || '',
                    // Hallazgo QA 2026-09-10 (ronda 3): CreatingOrgPage ahora usa
                    // AddressAutocomplete y puede guardar coords junto al resto
                    // de pendingRestaurantData; propagarlas si están presentes.
                    ...(pendingRestaurant.location ? { location: pendingRestaurant.location } : {}),
                  });
                  const newRestaurantId =
                    createRestResponse?.restaurantId ||
                    createRestResponse?.restaurant?.restaurantId;
                  if (newRestaurantId) {
                    storage.set('selectedRestaurantId', newRestaurantId);
                    storage.set('selectedRestaurantName', pendingRestaurant.restaurantName);
                    activeRestaurants = [{ restaurantId: newRestaurantId, status: 'active' }];
                  }
                }
              } catch (pendingRestError) {
                console.error(
                  '[AuthContext] No se pudo crear el restaurante pendiente (Google signup):',
                  pendingRestError.message
                );
                // Si falla, no rompemos el login — RequireRestaurant lo mandara a /restaurant/new.
              }
              storage.remove(PENDING_RESTAURANT_KEY);
            }
          }

          if (activeRestaurants.length > 0) {
            const currentSelection = activeRestaurants.find(
              (r) => r.restaurantId === savedRestaurantId
            );
            if (!currentSelection) {
              const firstActive =
                activeRestaurants.find((r) => r.status === 'active') || activeRestaurants[0];
              storage.set('selectedRestaurantId', firstActive.restaurantId);
              if (firstActive.name) storage.set('selectedRestaurantName', firstActive.name);
            }
          }
        } catch (restError) {
          console.error('[AuthContext] Error obteniendo restaurantes:', restError.message);
        }
      } catch (orgError) {
        console.error('[AuthContext] Error obteniendo organización:', orgError.message);
        setOrganization(null);
        storage.remove(ORGANIZATION_ID_KEY);
      }
    } catch (err) {
      // No authenticated user — expected on first load
      setUser(null);
      setOrganization(null);
      setRole(null);
      setGroups([]);
      setRoleWasDefaulted(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        await checkUser();
        return { success: true };
      } else if (result.nextStep) {
        const { signInStep } = result.nextStep;

        // Caso especial: Usuario necesita cambiar contraseña
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setPendingPasswordChallenge({ username: email });
          return {
            success: false,
            requiresNewPassword: true,
            message: 'Debes cambiar tu contraseña temporal',
          };
        }

        return {
          success: false,
          nextStep: signInStep,
          message: 'Acción adicional requerida',
        };
      }
    } catch (err) {
      console.error('Error en login:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
        requiresConfirmation: err.name === 'UserNotConfirmedException',
        email: email,
      };
    }
  };

  const confirmNewPassword = async (newPassword, name) => {
    try {
      setError(null);

      if (!pendingPasswordChallenge) {
        throw new Error('No hay un desafío de contraseña pendiente');
      }

      // Confirming new password challenge

      // Confirmar el cambio de contraseña con Amplify
      // Incluir atributos requeridos por Cognito
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: name,
          },
        },
      });

      if (result.isSignedIn) {
        // Password changed successfully
        setPendingPasswordChallenge(null);
        await checkUser();
        return { success: true };
      } else {
        console.warn('[AuthContext] Contraseña cambiada pero requiere más pasos:', result.nextStep);
        return {
          success: false,
          nextStep: result.nextStep?.signInStep,
          message: 'Se requieren pasos adicionales',
        };
      }
    } catch (err) {
      console.error('Error confirmando nueva contraseña:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  const register = async (email, password, name, userAttributes = {}) => {
    try {
      setError(null);

      // Preparar atributos de Cognito (solo los soportados por defecto)
      const cognitoAttributes = {
        email,
        name,
      };

      // Si hay phone number, agregarlo
      if (userAttributes.phone) {
        cognitoAttributes.phone_number = userAttributes.phone;
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: cognitoAttributes,
        },
      });

      // Guardar todos los datos adicionales en localStorage para usarlos después de confirmar
      // Esto incluye organizationName, CUIT, dirección, etc.
      const pendingData = {
        email: email,
        name: name,
        organizationName: userAttributes.organizationName || name,
        phone: userAttributes.phone || '',
        cuit: userAttributes.cuit || '',
        address: userAttributes.address || '',
        city: userAttributes.city || '',
        province: userAttributes.province || '',
        country: userAttributes.country || '',
        postalCode: userAttributes.postalCode || '',
        accountType: userAttributes.accountType || 'person',
        companyName: userAttributes.companyName || '',
        restaurantName: userAttributes.restaurantName || '',
        restaurantSlug: userAttributes.restaurantSlug || '',
      };

      storage.set(PENDING_ORGANIZATION_KEY, JSON.stringify(pendingData));

      return {
        success: true,
        userId: result.userId,
        nextStep: result.nextStep.signUpStep,
      };
    } catch (err) {
      console.error('Error en registro:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
      };
    }
  };

  const confirmRegistration = async (email, code) => {
    try {
      setError(null);
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      return { success: true };
    } catch (err) {
      console.error('Error confirmando registro:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
      };
    }
  };

  const resendConfirmationCode = async (email) => {
    try {
      setError(null);
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (err) {
      console.error('Error reenviando código de confirmación:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
      };
    }
  };

  const createOrganizationAfterSignup = async () => {
    try {
      // Recuperar datos pendientes de localStorage
      const pendingDataString = storage.get(PENDING_ORGANIZATION_KEY);
      if (!pendingDataString) {
        return { success: false, error: 'No hay datos de organización pendientes' };
      }

      const pendingData = JSON.parse(pendingDataString);
      const result = await createOrganizationAPI(pendingData);

      // Actualizar el estado local
      if (result.organization) {
        setOrganization(result.organization);

        // Guardar organizationId en localStorage
        if (result.organization.organizationId) {
          storage.set(ORGANIZATION_ID_KEY, result.organization.organizationId);
        }
      }

      // Limpiar datos pendientes
      storage.remove(PENDING_ORGANIZATION_KEY);

      return { success: true, organization: result.organization };
    } catch (err) {
      console.error('Error creando organización:', err);
      return {
        success: false,
        error: err.response?.data?.message || err.message,
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      await resetPassword({ username: email });
      return { success: true };
    } catch (err) {
      console.error('Error en forgot password:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return { success: false, error: translatedError };
    }
  };

  const confirmForgotPassword = async (email, code, newPassword) => {
    try {
      setError(null);
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { success: true };
    } catch (err) {
      console.error('Error confirmando reset password:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return { success: false, error: translatedError };
    }
  };

  const clearPendingOrganization = () => {
    storage.remove(PENDING_ORGANIZATION_KEY);
  };

  const hasPendingOrganization = () => {
    return storage.get(PENDING_ORGANIZATION_KEY) !== null;
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setOrganization(null);
      // Limpiar datos persistidos (org + restaurant + auth cache)
      storage.remove(ORGANIZATION_ID_KEY);
      storage.remove(PENDING_ORGANIZATION_KEY);
      storage.remove('selectedOrganizationId');
      storage.remove('selectedOrganizationName');
      storage.remove('userRole');
      storage.remove('allRestaurants');
      storage.remove('selectedRestaurantId');
      storage.remove('selectedRestaurantName');
      return { success: true };
    } catch (err) {
      console.error('Error en logout:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (err) {
      console.error('Error obteniendo token:', err);
      return null;
    }
  };

  const value = {
    user,
    organization,
    loading,
    error,
    role,
    groups,
    roleWasDefaulted,
    login,
    logout,
    register,
    confirmRegistration,
    resendConfirmationCode,
    confirmNewPassword,
    forgotPassword,
    confirmForgotPassword,
    checkUser,
    getAccessToken,
    createOrganizationAfterSignup,
    clearPendingOrganization,
    hasPendingOrganization,
    pendingPasswordChallenge,
    authLoading: loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
